import React, { useEffect } from 'react';

import {
  CTA,
  HandleSelectEvent,
  InputAppearanceValue,
  Select,
} from '@volkswagen-onehub/components-core';
import { ThemeProvider } from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { AddressVerificationFail } from '../../../../shared/components/address-verification-fail/AddressVerificationFail';
import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import { NoTranslateTextInput } from '../../../../shared/components/no-translate-text-input';
import { provinceList } from '../../../../shared/utils/provinceList';
import { stateList } from '../../../../shared/utils/stateList';
import { StyledButtonsContainer, StyledFormWrapper, StyledInputsContainer } from './styled';
import { useAddressVerification } from '../../../../shared/hooks/use-address-verification';
import { useFormErrorHandling } from '../../../../shared/hooks/use-form-error-handling';
import { useGoogleMapsApi } from '../../../../shared/hooks/use-google-maps-api';
import { UserData } from '../../../../shared/typings/main';
import {
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';

interface EditHomeAddressFormViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  isCanada: boolean;
  isFrench: boolean;
  updateUserProfile: (data: Partial<UserData>) => void;
  user: UserData;
  onSuccess: () => void;
  onChange: () => void;
  baseUrl?: string;
}

export const EditHomeAddressFormView: React.FC<EditHomeAddressFormViewProps> = ({
  authService,
  translationHandler,
  trackingManager,
  localeCode,
  isCanada,
  isFrench,
  updateUserProfile,
  user,
  onChange,
  onSuccess,
  baseUrl
}) => {

  useEffect(() => {
    if (isCanada) {
      onVWBasicLayerLoadEvent(trackingManager, 'Edit Address');
    }
  }, []);

  const { address } = user;

  const {
    setActiveErrorModal,
    setErrorInformation,
    isSubmitting,
    setIsSubmitting,
  } = useFormErrorHandling({ authService });

  const { googleAPILoaded } = useGoogleMapsApi({
    authService,
    localeCode,
    baseUrl,
    setActiveErrorModal,
    setErrorInformation,
  });

  const validationSchema = Yup.object({
    address1: Yup.string()
      .matches(/^[a-zA-Z0-9\s]*$/, {
        message: translationHandler('accountSettings.edit.address1.error.invalid'),
        excludeEmptyString: true,
      })
      .required(translationHandler('accountSettings.edit.address1.error.empty')),
    address2: Yup.string(),
    city: Yup.string()
      .matches(/^[a-zA-Z\s]*$/, {
        message: translationHandler('accountSettings.edit.city.error.invalid'),
        excludeEmptyString: true,
      })
      .required(translationHandler('accountSettings.edit.city.error.empty')),
    state: Yup.string(),
    zipCode: isCanada
      ? Yup.string()
        .matches(/^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/, {
          message: translationHandler('accountSettings.edit.postalCode.error.invalid'),
          excludeEmptyString: true,
        })
        .required(translationHandler('accountSettings.edit.postalCode.error.empty'))
      : Yup.string()
        .matches(/^\d{5}$/, {
          message: translationHandler('accountSettings.edit.zip.error.invalid'),
          excludeEmptyString: true,
        })
        .required(translationHandler('accountSettings.edit.zip.error.empty')),
  });

  const formik = useFormik({
    initialValues: {
      address1: address.address1 || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zip || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);

      // Track button click for Update address CTA - handle errors separately
      try {
        onVWBasicButtonClickWithProcessEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Home Address',
          StepNumber: '4.2',
        }, { name: 'Update address' });
      } catch (error) {
        console.warn('Error tracking button click:', error);
      }
      try {
        let addresValidation: any;
        const payloadObject: Record<string, any> = {
          firstName: user.firstName,
          lastName: user.lastName,
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
        };
        const payloadForAddressVerification: Record<string, any> = {
          address1: { value: values.address1 },
          address2: { value: values.address2 },
          city: { value: values.city },
          state: { value: values.state },
          zipCode: { value: values.zipCode },
          postalCode: { value: values.zipCode },
          firstName: { value: user.firstName },
          lastName: { value: user.lastName },
        };
        if (!withoutAddressVerification) {
          addresValidation = isCanada
            ? await authService.addressvalidationCanada(payloadForAddressVerification)
            : await authService.addressvalidation(payloadForAddressVerification);

          if (
            !addresValidation ||
            (addresValidation &&
              addresValidation.data &&
              addresValidation.data.data &&
              addresValidation.data.data.verificationStatus === 'Invalid')
          )
            setAddressVerificationFail(true);
        }
        if (
          withoutAddressVerification ||
          (addresValidation &&
            addresValidation.data &&
            addresValidation.data.data &&
            addresValidation.data.data.verificationStatus !== 'Invalid')
        ) {
          await authService.updateProfileDynamicGqlRequest(payloadObject, localeCode);
          updateUserProfile({ address: { ...user.address, ...values } });
          onSuccess();
        }
      } catch (error) {
        console.error('Error updating user data:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const {
    addressInputRef,
    addressVerificationFail,
    setAddressVerificationFail,
    withoutAddressVerification,
    setWithoutAddressVerification,
  } = useAddressVerification({
    googleAPILoaded,
    formik,
    setActiveErrorModal,
    setErrorInformation,
  });

  useEffect(() => {
    if (formik.dirty && onChange) onChange();
  }, [formik.values, onChange]);

  const getFieldProps = (
    name: string,
  ): {
    name: string;
    onChange: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    onBlur: (e: React.SyntheticEvent<HTMLInputElement>) => void;
    value: any;
    message: string | undefined;
    appearance: InputAppearanceValue | undefined;
  } => ({
    name,
    onChange: (e) => {
      formik.handleChange(e);
    },
    onBlur: formik.handleBlur,
    value: formik.values[name],
    message:
      formik.touched[name] && formik.errors[name]
        ? formik.errors[name]
        : undefined,
    appearance:
      formik.touched[name] && formik.errors[name]
        ? ('error' as InputAppearanceValue)
        : undefined,
  });

  const handleSelectChange: HandleSelectEvent = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;
    formik.setFieldValue('state', value);
    formik.setFieldTouched('state', true);

    // Force validation to run after value is set
    Promise.resolve().then(() => { formik.validateForm(); });
  };

  const isButtonDisabled =
    Object.keys(formik.errors).length > 0 ||
    !formik.dirty ||
    formik.isSubmitting ||
    isSubmitting ||
    (addressVerificationFail && !withoutAddressVerification);

  return (
    <ThemeProvider theme="main">
      <StyledFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsContainer className="inputs-container">
            <NoTranslateTextInput
              {...getFieldProps('address1')}
              aria-label={translationHandler('accountSettings.edit.label.address1')}
              aria-required="true"
              label={translationHandler('accountSettings.edit.label.address1')}
              required
              ref={addressInputRef}
            />
            <NoTranslateTextInput
              {...getFieldProps('address2')}
              aria-label={translationHandler('accountSettings.edit.label.address2')}
              aria-required="true"
              label={translationHandler('accountSettings.edit.label.address2')}
            />
            <NoTranslateTextInput
              {...getFieldProps('city')}
              aria-label={translationHandler('registration.form.city.label')}
              aria-required="true"
              label={translationHandler('registration.form.city.label')}
              required
            />
            <Select
              appearance={formik.touched.state && formik.errors.state && formik.values.state === '' ? ('error' as any) : undefined}
              aria-label={isCanada ? 'Select province component' : 'Select state component'}
              aria-required="true"
              defaultValue=""
              isFloating
              message={formik.touched.state && formik.errors.state && formik.values.state === '' ? translationHandler(formik.errors.state) : undefined}
              onBlur={() => { formik.setFieldTouched('state', true); }}
              onChange={handleSelectChange}
              required
              value={formik.values.state}
              name="state"
              label={translationHandler(isCanada ? 'accountSettings.edit.province' : 'accountSettings.edit.state')}
            >
              <option value="">
                {translationHandler(isCanada ? 'registration.form.select.province.label' : 'registration.form.select.state.label')}
              </option>
              {isCanada
                ? provinceList.map((item, index: number) => (
                  <option key={`Province-list-option-${index + 1}`} value={item.code}>
                    {applyTextTransformations(isFrench ? item.frenchName : item.name, [isFrench ? item.frenchName : item.name])}
                  </option>
                ))
                : stateList.map((item: any, index: number) => (
                  <option key={`State-list-option-${index + 1}`} value={item.code}>
                    {applyTextTransformations(item.name, [item.name])}
                  </option>
                ))}
            </Select>

            <NoTranslateTextInput
              {...getFieldProps('zipCode')}
              aria-label={translationHandler('accountSettings.edit.zip')}
              aria-required="true"
              label={translationHandler('accountSettings.edit.zip')}
              maxLength={isCanada ? 7 : 5}
              required
            />
          </StyledInputsContainer>

          {addressVerificationFail && (
            <AddressVerificationFail
              withoutAddressVerification={withoutAddressVerification}
              setWithoutAddressVerification={setWithoutAddressVerification}
              translationHandler={translationHandler}
            />
          )}
          <StyledButtonsContainer>
            <CTA
              stretchContent
              aria-label="Save button"
              disabled={isButtonDisabled}
              emphasis="primary"
              tag="button"
              type="submit"
            >
              {translationHandler('accountSettings.edit.homeAddress')}
            </CTA>
          </StyledButtonsContainer>
        </form>
      </StyledFormWrapper>
    </ThemeProvider>
  );
};